import React, { useEffect, useState } from 'react';
import { Briefcase, TrendingUp, TrendingDown, Clock, Activity } from 'lucide-react';
import { motion } from 'framer-motion';

const Portfolio = ({ userData }: { userData?: any }) => {
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    if (userData && userData.id) {
      // URL apna local wala hi rakhna
      // fetch(`http://localhost:10001/api/profile/${userData.id}/portfolio-history`)
      fetch(`https://motilalalgo.com/api/profile/${userData.id}/portfolio-history`)
        .then(res => res.json())
        .then(data => setHistory(data))
        .catch(err => console.error(err));
    }
  }, [userData]);

  if (!userData) return null;

  const currentBalance = parseFloat(userData.wallet_balance || 0);
  
  // Total Profit/Loss ab trading history ke profit_loss amount se calculate hoga
  const totalProfit = history.filter(h => parseFloat(h.profit_loss) > 0).reduce((acc, curr) => acc + parseFloat(curr.profit_loss), 0);
  const totalLoss = history.filter(h => parseFloat(h.profit_loss) < 0).reduce((acc, curr) => acc + Math.abs(parseFloat(curr.profit_loss)), 0);

  return (
    <div className="max-w-5xl mx-auto space-y-8 font-[Urbanist] pb-10">
      
      {/* Animated Heading */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.h1 
          className="text-4xl md:text-5xl font-black bg-gradient-to-r from-[#3b36a4] to-[#3b82f6] text-transparent bg-clip-text pb-2"
        >
          Your Portfolio
        </motion.h1>
        <p className="text-[#64748b] font-medium mt-1 text-sm md:text-base">Track your total investments and P&L history.</p>
      </motion.div>

      {/* Portfolio Metrics - Animated Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div whileHover={{ y: -5 }} className="bg-white border border-[#f1f5f9] rounded-[24px] md:rounded-[30px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden group hover:border-[#bfdbfe] transition-all cursor-pointer">
          <div className="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:scale-110 transition-transform"><Briefcase size={100} /></div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-[#eff6ff] rounded-[14px]"><Briefcase className="text-[#3b82f6]" size={20} strokeWidth={2.5} /></div>
            <h3 className="text-[#64748b] font-bold uppercase text-[11px] tracking-widest">Total Investment</h3>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-[#0f172a]">₹{currentBalance.toFixed(2)}</h2>
        </motion.div>
        
        <motion.div whileHover={{ y: -5 }} className="bg-white border border-[#f1f5f9] rounded-[24px] md:rounded-[30px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-emerald-200 transition-all cursor-pointer">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-emerald-50 rounded-[14px]"><TrendingUp className="text-emerald-600" size={20} strokeWidth={2.5} /></div>
            <h3 className="text-[#64748b] font-bold uppercase text-[11px] tracking-widest">Lifetime Profit</h3>
          </div>
          <h2 className="text-3xl font-black text-emerald-600">+₹{totalProfit.toFixed(2)}</h2>
        </motion.div>

        <motion.div whileHover={{ y: -5 }} className="bg-white border border-[#f1f5f9] rounded-[24px] md:rounded-[30px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-red-200 transition-all cursor-pointer">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-red-50 rounded-[14px]"><TrendingDown className="text-red-600" size={20} strokeWidth={2.5} /></div>
            <h3 className="text-[#64748b] font-bold uppercase text-[11px] tracking-widest">Lifetime Loss</h3>
          </div>
          <h2 className="text-3xl font-black text-red-600">-₹{totalLoss.toFixed(2)}</h2>
        </motion.div>
      </div>

      {/* Profit & Loss History Ledger */}
      <div className="bg-white border border-[#f1f5f9] rounded-[24px] md:rounded-[30px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="p-6 border-b border-[#f1f5f9] flex items-center gap-3 bg-[#f8fafc]">
          <div className="p-2.5 bg-white rounded-[14px] shadow-sm border border-[#f1f5f9]"><Clock className="text-[#3b82f6]" size={20} strokeWidth={2.5} /></div>
          <h3 className="text-xl font-black text-[#0f172a]">Profit & Loss History</h3>
        </div>
        
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[700px]">
            <thead>
              <tr className="bg-white text-[#94a3b8] text-[11px] uppercase tracking-widest font-bold border-b border-[#f1f5f9]">
                <th className="p-5 pl-6">Date & Time</th>
                <th className="p-5">Stock Name</th>
                <th className="p-5 text-center">Action</th>
                <th className="p-5 text-right pr-6">Amount (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f8fafc]">
              {history.map((record, idx) => {
                const plAmount = parseFloat(record.profit_loss || 0);
                
                // Agar 0 hai toh history me mat dikhao
                if (plAmount === 0) return null; 
                
                const isProfit = plAmount > 0;

                return (
                  <tr key={idx} className="hover:bg-[#f8fafc] transition-colors group">
                    <td className="p-5 pl-6">
                      <p className="font-bold text-[#0f172a] text-sm">{new Date(record.created_at).toLocaleDateString('en-GB')}</p>
                      <p className="text-[11px] font-semibold text-[#94a3b8] mt-0.5">{new Date(record.created_at).toLocaleTimeString()}</p>
                    </td>
                    <td className="p-5">
                      <p className="font-black text-[#0f172a] text-sm">{record.stock_name}</p>
                    </td>
                    <td className="p-5 text-center">
                      <span className={`inline-flex items-center justify-center font-black text-[10px] px-3 py-1.5 rounded-full tracking-widest ${isProfit ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                        {isProfit ? 'PROFIT' : 'LOSS'}
                      </span>
                    </td>
                    <td className={`p-5 text-right font-black text-lg pr-6 ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                      {isProfit ? '+' : '-'}₹{Math.abs(plAmount).toFixed(2)}
                    </td>
                  </tr>
                );
              })}

              {/* No Data State */}
              {history.filter(r => parseFloat(r.profit_loss) !== 0).length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-16">
                    <div className="w-16 h-16 bg-[#f8fafc] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#f1f5f9]">
                      <Activity className="text-[#cbd5e1]" size={24} strokeWidth={2.5} />
                    </div>
                    {/* <p className="font-black text-[#0f172a] text-lg mb-1">No P&L Activity Yet</p> */}
                    {/* <p className="text-sm font-medium text-[#64748b]">Profits and losses updated by admin will appear here.</p> */}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;